import type { ItineraryResultsStore } from '$lib/components/itinerary/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { PoisStoreContent } from '$routes/(map)/types';
import type { PageLoadEvent as RouteCorridorPageLoadEvent } from './$types';
import type { PageLoadEvent as RouteCorridorFiltersPageLoadEvent } from './filters/$types';
import type { PageLoadEvent as RouteCorridorPoiPageLoadEvent } from './poi/$types';
import { getCorridorPoisStore } from '$houdini';
import { defaultMapBoundsByCountry } from '$lib/components/map/constants';
import { getWaypointCoordinates } from '$lib/components/search/itinerary/ItineraryStore';
import { defaultPoiCategories } from '$lib/constants/searchParams';
import { getPartnersDeviceType } from '$lib/utils/getPartnersDeviceType';
import { filterUndefined } from '$lib/utils/predicates';
import { enrichPoisStoreWithSelectedPoiData, getPoiFilters } from '$routes/(map)/loadPois';
import { getCountryFromLocale } from '@viamichelin/business';
import { derived, get, type Readable } from 'svelte/store';

interface GetCorridorDataArgs {
	event:
		| RouteCorridorPageLoadEvent
		| RouteCorridorPoiPageLoadEvent
		| RouteCorridorFiltersPageLoadEvent;
	searchParams: SearchParams;
	itineraryResultsStore: ItineraryResultsStore;
}

export async function getCorridorData({
	event,
	searchParams,
	itineraryResultsStore,
}: GetCorridorDataArgs): Promise<{
		poisStore: Readable<Omit<PoisStoreContent, 'selectedPoiData'>>;
		corridorPolygonStore: Readable<number[][][] | undefined>;
		poisStoreWithoutSelectedPoi: Readable<PoisStoreContent>;
	}> {
	const {
		corridor,
		poiCategories,
		filters,
		tripConstraint,
		travelMode,
		distanceSystem,
		car,
		currency,
		energyPrice,
		itinerary,
		bounds,
		selectedRoute,
		selectedPoi,
	} = searchParams;

	const { locale } = await event.parent();

	const coordinates = await Promise.all((itinerary ?? []).map(getWaypointCoordinates));
	// eslint-disable-next-line new-cap
	const corridorPoisStore = new getCorridorPoisStore();

	itineraryResultsStore.lastFetchPromise().then(() => {
		const route = get(itineraryResultsStore).routes[selectedRoute ?? 0];

		if (!corridor?.category) {
			return undefined;
		}

		const poiCategory = corridor.category;

		corridorPoisStore.fetch({
			blocking: false,
			event,
			variables: {
				input: {
					category: poiCategory,
					radius: corridor.radius ? corridor.radius * 1000 : undefined,
					poiFilters: getPoiFilters(filters),
					routeIdentifier: route.identifier,
					legDistances: route.legDistances,
					itineraryInput: {
						coordinates: coordinates.filter(filterUndefined),
						carId: car?.id,
						constraint: tripConstraint,
						distanceSystem,
						mode: travelMode,
						device: getPartnersDeviceType(),
						energyCost: energyPrice,
						currency,
					},
				},
			},
		});
	});

	const poisStoreWithoutSelectedPoi = derived(corridorPoisStore, ($corridorPoisStore) => {
		const mapBounds = bounds ?? defaultMapBoundsByCountry[getCountryFromLocale(locale)];
		const poiCategoriesValue = poiCategories ?? defaultPoiCategories;

		if (!corridor?.category) {
			return {
				poisList: [],
				poisCount: 0,
				pageCount: 0,
				mapBounds,
				poiCategories: poiCategoriesValue,
				corridorCategory: undefined,
				fetching: false,
				hasError: false,
			};
		}

		if (
			!$corridorPoisStore.data
			|| $corridorPoisStore.data.getCorridorPois.__typename === 'GetCorridorPoisNotFoundResult'
		) {
			return {
				poisList: [],
				poisCount: 0,
				pageCount: 0,
				mapBounds,
				poiCategories: poiCategoriesValue,
				corridorCategory: corridor.category,
				fetching: $corridorPoisStore.fetching,
				hasError: !!$corridorPoisStore.errors,
			};
		}

		const { items, pageCount, totalCount, availabilityStatus }
			= $corridorPoisStore.data.getCorridorPois.poiList;

		return {
			availabilityStatus: availabilityStatus ?? undefined,
			poisList: items ?? [],
			poisCount: totalCount,
			pageCount,
			mapBounds,
			poiCategories: poiCategoriesValue,
			corridorCategory: corridor.category,
			fetching: $corridorPoisStore.fetching,
			hasError: !!$corridorPoisStore.errors,
		};
	});

	return {
		poisStore: await enrichPoisStoreWithSelectedPoiData(
			poisStoreWithoutSelectedPoi,
			selectedPoi,
			event,
		),
		poisStoreWithoutSelectedPoi,
		corridorPolygonStore: derived(corridorPoisStore, ($corridorPoisStore) => {
			if (
				!$corridorPoisStore.data
				|| $corridorPoisStore.data.getCorridorPois.__typename === 'GetCorridorPoisNotFoundResult'
			) {
				return undefined;
			}

			return $corridorPoisStore.data.getCorridorPois.corridorPolygon.coordinates;
		}),
	};
}
